import React, {useEffect, useState, useCallback, useRef} from 'react';
import {Box, Typography, Button, Snackbar, Alert} from '@mui/material';
import {useSelector} from 'react-redux';
import {useAuthFrontendApis} from '@propelauth/frontend-apis-react';
import {useTheme} from '@mui/material/styles';
import {useNavigate} from 'react-router-dom';
import {Plus} from 'lucide-react';
import InviteUserModal from './InviteUserModal';
import ParentOrgMembers from './ParentOrgMembers';

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
    minHeight: '48px',
  },
  title: {
    fontSize: '26px',
    fontWeight: 700,
    lineHeight: '130%',
    letterSpacing: '-0.24px',
    color: '#162523',
  },
};

const OrganizationUsersPage = () => {
  const {parentOrg} = useSelector((state) => state.session);
  const {fetchOrgSettings} = useAuthFrontendApis();
  const theme = useTheme();
  const navigate = useNavigate();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [orgSettings, setOrgSettings] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const parentOrgMembersRef = useRef();
  const pageTitle = parentOrg
    ? `${parentOrg.orgName} Users`
    : 'Organization Users';

  // Check if user has admin permissions for the parent organization
  const hasOwnerPermissions =
    parentOrg &&
    (parentOrg.userAssignedRole === 'InScope Admin' ||
      parentOrg.userAssignedRole === 'Owner');

  // Fetch parent org settings for SAML check
  useEffect(() => {
    if (!parentOrg?.orgId) return;

    const load = async () => {
      try {
        const response = await fetchOrgSettings(parentOrg.orgId);
        await response.handle({
          success: (data) => setOrgSettings(data),
          orgNotFound: () => setOrgSettings(null),
          orgsNotEnabled: () => setOrgSettings(null),
          unauthorized: () => setOrgSettings(null),
          unexpectedOrUnhandled: () => setOrgSettings(null),
        });
      } catch (_) {
        setOrgSettings(null);
      }
    };
    load();
  }, [parentOrg?.orgId, fetchOrgSettings]);

  const shouldShowInviteButton = orgSettings?.is_saml_enabled === false;

  const handleInviteClick = () => setInviteModalOpen(true);

  const handleShowSnackbar = useCallback(({message, severity}) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });

    // If it's a successful invitation, refresh pending invites
    if (severity === 'success' && message.includes('Invitation sent')) {
      setTimeout(() => {
        parentOrgMembersRef.current?.refreshPendingInvites();
      }, 100);
    }
  }, []);

  if (!parentOrg) {
    return (
      <Box sx={styles.container}>
        <Typography variant="h4">Organization not found</Typography>
      </Box>
    );
  }

  if (!hasOwnerPermissions) {
    return (
      <Box sx={styles.container}>
        <Typography variant="h4" sx={{color: '#D73E3E', marginBottom: '16px'}}>
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{color: '#666', marginBottom: '24px'}}>
          You do not have permission to access settings for this organization.
        </Typography>
        <Button
          onClick={() => navigate('/settings')}
          sx={theme.button.primary.medium}
        >
          Back to Settings
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {/* Page Header */}
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{pageTitle}</Typography>
        <Box>
          {shouldShowInviteButton && parentOrg && (
            <Button
              onClick={handleInviteClick}
              startIcon={<Plus size={16} />}
              sx={{
                ...theme.button.primary.medium,
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                width: '140px',
                alignSelf: 'flex-start',
                marginTop: '2px',
              }}
            >
              Invite user
            </Button>
          )}
        </Box>
      </Box>

      {/* Parent Org Members Table */}
      <ParentOrgMembers
        ref={parentOrgMembersRef}
        isSAMLEnabled={orgSettings?.is_saml_enabled === true}
      />

      {/* Invite User Modal */}
      {parentOrg && (
        <InviteUserModal
          open={inviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          orgId={parentOrg.orgId}
          orgName={parentOrg.orgName}
          onShowSnackbar={handleShowSnackbar}
          forceRole={'Reviewer'}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        style={{marginTop: '35px'}}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            backgroundColor: '#FFFFFF',
            color: '#000000',
            border: '1px solid #E1E5E4',
            borderRadius: '8px',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrganizationUsersPage;
